'use client';

import { useTranslations } from 'next-intl';

import Home from '@/assets/icons/menu/home.svg';
import Preds from '@/assets/icons/menu/preds.svg';
import Profile from '@/assets/icons/menu/profile.svg';
import { Button } from '@/components/ui/button';
import useKeyboard from '@/hooks/useKeyboard';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { Link, usePathname, useRouter } from '@/i18n/navigation';
import { cn } from '@/lib/utils/cn';
import { getPoolsNavHref } from '@/lib/utils/pools';
import { C_Routes } from '@/ts/constants/C_Routes';
import { E_Routes } from '@/ts/enums/E_Routes';

interface I_BottomMenuProps {
  allPresalesEnded?: boolean;
  firstPoolSlug?: string | null;
  isNotFound?: boolean;
}

function BottomMenu({
  allPresalesEnded = false,
  firstPoolSlug = null
}: I_BottomMenuProps) {
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations('bottomMenu');
  const { isFullyLoggedIn, openWalletConnect } = useWalletConnection();

  // Determine active states
  const isHomeActive = pathname === '/';
  const isPoolsActive =
    pathname.startsWith(C_Routes[E_Routes.POOLS_PRESALE].link) ||
    (allPresalesEnded && pathname.startsWith(C_Routes[E_Routes.POOLS].link));
  const isProfileActive = pathname.startsWith(C_Routes[E_Routes.PROFILE].link);
  const isProfileHistory =
    pathname.startsWith(C_Routes[E_Routes.PROFILE_REFERRALS].link) ||
    pathname.startsWith(C_Routes[E_Routes.PROFILE_PREDICTIONS].link) ||
    pathname.startsWith(C_Routes[E_Routes.PROFILE_POOLS].link);

  const poolsHref = getPoolsNavHref({
    hasActivePresale: !allPresalesEnded,
    firstPoolSlug,
    fallbackPresaleHref: C_Routes[E_Routes.POOLS_PRESALE].link
  });

  const isKeyboardOpen = useKeyboard();

  const handleProfileClick = () => {
    if (isFullyLoggedIn) {
      router.push(C_Routes[E_Routes.PROFILE].link);
    } else {
      openWalletConnect();
    }
  };
  return (
    <nav
      className={cn(
        'md:hidden fixed inset-x-0 bg-bottom-menu bottom-0 z-50 safe-area-pb ',
        ' transform-gpu will-change-transform',
        (isProfileHistory || isKeyboardOpen) && 'hidden'
      )}
    >
      <ul className="grid h-23 grid-cols-3 items-center bg-bottom-menu">
        <li className="menu__item flex justify-center">
          <Link
            href={C_Routes[E_Routes.HOME].link}
            className="menu__link inline-flex flex-col justify-start items-center gap-2"
          >
            <Home color={isHomeActive ? 'var(--secondary)' : 'var(--muted)'} />
            <span
              className={cn(
                'text-center justify-start text-xs font-bold leading-none',
                isHomeActive ? 'text-foreground' : 'text-muted-foreground'
              )}
            >
              {t('home')}
            </span>
          </Link>
        </li>
        <li className="menu__item flex justify-center">
          <Link
            href={poolsHref}
            className="menu__link inline-flex flex-col justify-start items-center gap-2"
          >
            <Preds
              color={isPoolsActive ? 'var(--secondary)' : 'var(--muted)'}
            />
            <span
              className={cn(
                'text-center justify-start text-xs font-bold leading-none',
                isPoolsActive ? 'text-foreground' : 'text-muted-foreground'
              )}
            >
              {t('pools')}
            </span>
          </Link>
        </li>
        <li className="menu__item flex justify-center">
          <Button
            onClick={handleProfileClick}
            asChild
            className="bg-transparent p-0 menu__link inline-flex flex-col justify-start items-center gap-2 capitalize
              focus:ring-0 active:ring-0 focus:border-0 active:border-0 "
          >
            <Profile
              color={isProfileActive ? 'var(--secondary)' : 'var(--muted)'}
            />
            <span
              className={cn(
                'text-center justify-start text-xs font-bold leading-none',
                isProfileActive ? 'text-foreground' : 'text-muted-foreground'
              )}
            >
              {t('profile')}
            </span>
          </Button>
        </li>
      </ul>
    </nav>
  );
}

export default BottomMenu;
